import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenu, HiX, HiChevronRight } from 'react-icons/hi';
import { NAV_LINKS } from '../utils/constants';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleLinkClick = (path) => {
    setIsMobileMenuOpen(false);
    if (path.startsWith('#')) {
      const element = document.querySelector(path);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const isActiveLink = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path.startsWith('#')) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200`}
        style={{
          backgroundColor: isScrolled ? '#F5F0E8' : '#F5F0E8',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '64px',
          }}>
            {/* Logo */}
            <Link
              to="/"
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
              onClick={() => handleLinkClick('/')}
            >
              <span style={{
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#1A1A1A',
                letterSpacing: '-0.02em',
              }}>
                Rishant
              </span>
              <span style={{
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 500,
                fontSize: '0.95rem',
                color: '#666666',
              }}>
                Technologies
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div style={{
              display: 'none',
              alignItems: 'center',
              gap: '4px',
            }} className="desktop-nav">
              {NAV_LINKS.map((link) => (
                <div key={link.name}>
                  {link.path.startsWith('#') ? (
                    <button
                      onClick={() => handleLinkClick(link.path)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '8px 14px',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        color: '#666666',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        transition: 'color 0.15s',
                        fontFamily: '"DM Sans", sans-serif',
                      }}
                      onMouseEnter={e => e.target.style.color = '#1A1A1A'}
                      onMouseLeave={e => e.target.style.color = '#666666'}
                    >
                      {link.name}
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      style={{
                        padding: '8px 14px',
                        fontSize: '0.9rem',
                        fontWeight: isActiveLink(link.path) ? 600 : 500,
                        color: isActiveLink(link.path) ? '#1A1A1A' : '#666666',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        transition: 'color 0.15s',
                        fontFamily: '"DM Sans", sans-serif',
                      }}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Right Side */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* CTA */}
              <Link
                to="/contact"
                className="desktop-nav"
                style={{
                  display: 'none',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 24px',
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  borderRadius: '999px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'background-color 0.15s',
                  fontFamily: '"DM Sans", sans-serif',
                }}
              >
                Book Free Audit
              </Link>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="mobile-menu-btn"
                style={{
                  display: 'none',
                  padding: '8px',
                  background: 'none',
                  border: 'none',
                  color: '#1A1A1A',
                  cursor: 'pointer',
                }}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <HiX size={24} /> : <HiMenu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.2)',
                zIndex: 40,
              }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              style={{
                position: 'fixed',
                top: '72px',
                left: '16px',
                right: '16px',
                background: '#FFFFFF',
                border: '1px solid var(--color-border)',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                zIndex: 50,
                padding: '16px',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {NAV_LINKS.map((link) => (
                  <div key={link.name}>
                    {link.path.startsWith('#') ? (
                      <button
                        onClick={() => handleLinkClick(link.path)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px 16px',
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: '#333333',
                          background: 'none',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontFamily: '"DM Sans", sans-serif',
                        }}
                      >
                        {link.name}
                      </button>
                    ) : (
                      <Link
                        to={link.path}
                        onClick={() => handleLinkClick(link.path)}
                        style={{
                          display: 'block',
                          padding: '12px 16px',
                          fontSize: '0.95rem',
                          fontWeight: isActiveLink(link.path) ? 600 : 500,
                          color: isActiveLink(link.path) ? '#1A1A1A' : '#333333',
                          textDecoration: 'none',
                          borderRadius: '8px',
                          backgroundColor: isActiveLink(link.path) ? '#F5F0E8' : 'transparent',
                          fontFamily: '"DM Sans", sans-serif',
                        }}
                      >
                        {link.name}
                      </Link>
                    )}
                  </div>
                ))}
                <div style={{ borderTop: '1px solid var(--color-border)', marginTop: '8px', paddingTop: '12px' }}>
                  <Link
                    to="/contact"
                    onClick={() => setIsMobileMenuOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '14px',
                      backgroundColor: '#1A1A1A',
                      color: '#FFFFFF',
                      borderRadius: '999px',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      textDecoration: 'none',
                      fontFamily: '"DM Sans", sans-serif',
                    }}
                  >
                    Book Free Audit <HiChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* CSS for responsive show/hide */}
      <style>{`
        .desktop-nav { display: none !important; }
        .mobile-menu-btn { display: flex !important; }
        @media (min-width: 1024px) {
          .desktop-nav { display: flex !important; }
          .mobile-menu-btn { display: none !important; }
        }
      `}</style>
    </>
  );
};

export default Navbar;
